/*
 * Quick Preview — panel inline (collection / search / FMD)
 * -----------------------------------------------------------------------------
 * Rendu par sections/main-product-quick-preview.liquid, fetché via
 * /products/{handle}?view=quick-preview et injecté dans le singleton
 * snippets/qp-singleton.liquid au clic sur `.quick-product__btn`. La
 * MutationObserver plus bas attrape l'injection et appelle bindView() pour
 * brancher variant switching, thumbs, ATC, zoom, sticky.
 */

(function () {
  'use strict';

  var bound = new WeakSet();
  var moneyFormat = null;

  // === Mode INLINE (pages avec grille produit : collection, search, FMD) ===
  // Si un singleton `[data-qp-singleton]` est présent, on prend la main sur
  // les clics des `.quick-product__btn` au lieu de laisser theme.Modals ouvrir
  // un drawer overlay. Le contenu est fetché et injecté dans le singleton ;
  // le state `data-qp-open` sur `[data-qp-layout]` pilote l'animation CSS
  // (pousse la grille vers la gauche en desktop, slide overlay en mobile).
  function setupInlineMode() {
    var layout = document.querySelector('[data-qp-layout]');
    var singleton = document.querySelector('[data-qp-singleton]');
    if (!layout || !singleton) return;
    var holder = singleton.querySelector('[data-qp-holder]');
    var closeBtn = singleton.querySelector('[data-qp-close]');
    if (!holder) return;

    // Mesure la hauteur du header pour que le panel commence en dessous.
    // CSS lit `--qp-top` sur :root pour positionner le panel.
    function syncHeaderHeight() {
      var header = document.querySelector('.site-header');
      if (!header) return;
      var h = header.offsetHeight;
      document.documentElement.style.setProperty('--qp-top', h + 'px');
    }
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);

    // Toggle `data-qp-secondary-menu` sur :root selon que le secondary menu
    // du header est affiché ou non — la topbar buffer (67px) n'a de sens que
    // pour s'aligner avec lui. Quand `#HeaderWrapper` reçoit la classe
    // `header-wrapper--sticky` (au scroll, via theme.js), le secondary menu
    // est translaté hors écran (`mint.css:1588`) ; on doit donc masquer la
    // topbar dans ce cas. Aussi : si le secondary menu n'existe pas ou est
    // caché en `display:none` (responsive `medium-down--hide`), on masque.
    function syncSecondaryMenuState() {
      var hw = document.getElementById('HeaderWrapper');
      var sm = document.querySelector('.secondary__menu');
      var visible = !!sm && sm.offsetHeight > 0 && (!hw || !hw.classList.contains('header-wrapper--sticky'));
      if (visible) {
        document.documentElement.setAttribute('data-qp-secondary-menu', '');
      } else {
        document.documentElement.removeAttribute('data-qp-secondary-menu');
      }
    }
    syncSecondaryMenuState();
    window.addEventListener('resize', syncSecondaryMenuState);

    var headerWrapper = document.getElementById('HeaderWrapper');
    if (headerWrapper && typeof MutationObserver !== 'undefined') {
      new MutationObserver(syncSecondaryMenuState).observe(headerWrapper, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    var cache = new Map();

    function fetchProductHtml(handle) {
      var url = '/products/' + handle + '?view=quick-preview';
      return fetch(url)
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('.product-section[data-product-handle="' + handle + '"]');
          return section ? section.outerHTML : '';
        });
    }

    // Le panel pousse `.qp-layout__main` (padding-right: 560px) → la topbar
    // de filtres rétrécit. Le JS de la topbar (theme.js initTopbarDropdowns →
    // calculateOverflow) ne re-mesure que sur `window.resize`. On dispatche
    // donc un faux resize après la fin de la transition CSS (360ms) pour
    // forcer la re-distribution des pills (visibles vs "Filtres avancés").
    function notifyLayoutChanged() {
      window.setTimeout(function () {
        try {
          window.dispatchEvent(new Event('resize'));
        } catch (e) {
          /* noop legacy browser */
        }
      }, 380);
    }

    function injectAndOpen(html) {
      holder.innerHTML = html;
      layout.setAttribute('data-qp-open', 'true');
      document.body.classList.add('qp-open');
      singleton.setAttribute('aria-hidden', 'false');
      notifyLayoutChanged();
      // bindView() sera appelé via le MutationObserver plus bas.
    }

    function openPanel(handle) {
      if (cache.has(handle)) {
        injectAndOpen(cache.get(handle));
        return;
      }
      fetchProductHtml(handle).then(function (html) {
        if (!html) return;
        cache.set(handle, html);
        injectAndOpen(html);
      });
    }

    function closePanel() {
      layout.setAttribute('data-qp-open', 'false');
      document.body.classList.remove('qp-open');
      singleton.setAttribute('aria-hidden', 'true');
      notifyLayoutChanged();
    }

    // Click sur n'importe quel `.quick-product__btn` du document.
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest('.quick-product__btn');
        if (!btn) return;
        e.preventDefault();
        var card = btn.closest('.grid-product');
        var handle = card && card.dataset.productHandle;
        if (handle) openPanel(handle);
      },
      true,
    );

    if (closeBtn) closeBtn.addEventListener('click', closePanel);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && layout.getAttribute('data-qp-open') === 'true') {
        closePanel();
      }
    });

    // Mouseover preload : alimente le cache pour rendre l'ouverture instantanée.
    document.addEventListener('mouseover', function (e) {
      var card = e.target.closest && e.target.closest('.grid-product');
      if (!card) return;
      var handle = card.dataset.productHandle;
      if (!handle || cache.has(handle)) return;
      cache.set(handle, ''); // marquer comme en cours pour dédoublonner
      fetchProductHtml(handle)
        .then(function (html) {
          cache.set(handle, html);
        })
        .catch(function () {
          cache.delete(handle);
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupInlineMode);
  } else {
    setupInlineMode();
  }

  // Le contenu QP est injecté dans le holder du singleton par injectAndOpen()
  // (mode inline). On binde chaque nouvelle vue via un MutationObserver sur
  // le document, ce qui découple le binding du flow d'injection.
  if (typeof MutationObserver === 'function') {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (!m.addedNodes || !m.addedNodes.length) continue;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('quick-preview')) {
            bindView(node);
          } else if (node.querySelector) {
            var inner = node.querySelector('.quick-preview');
            if (inner) bindView(inner);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function bindView(root) {
    if (!root || bound.has(root)) return;
    bound.add(root);

    moneyFormat = moneyFormat || root.getAttribute('data-money-format') || '{{amount}} €';

    // Les balises <script> injectées via innerHTML sont inertes. On les réexécute
    // pour faire fonctionner l'infrastructure greenleaze (iframe modal + listeners).
    executeInlineScripts(root);

    // Branche theme.AjaxProduct sur le <form action="/cart/add"> du QP. Cette
    // fonction (theme.js:2367) scanne `form.product-single__form` dans le scope
    // et instancie new theme.AjaxProduct(form) (idempotent).
    if (typeof theme !== 'undefined' && typeof theme.initAjaxProductForms === 'function') {
      theme.initAjaxProductForms(root);
    }

    var variants = parseVariants(root);
    var currentId = Number(root.querySelector('[data-qp-atc]')?.getAttribute('data-qp-variant-id')) || null;
    var currentVariant =
      variants.find(function (v) {
        return v.id === currentId;
      }) ||
      variants[0] ||
      null;

    // Branche theme.Variants (theme.js:639) sur les radios [data-variant-input]
    // du picker product-page-style. Met à jour le hidden [data-product-select]
    // et dispatche `variantChange` sur le container — on l'écoute pour rafraîchir
    // l'UI (price, ATC, image, hidden id input) via updateForVariant.
    wireVariants(root, variants, function (next) {
      currentVariant = next;
      updateForVariant(root, next);
    });

    // Init le zoom inline (wheel + pinch + pan) calqué sur la FP. Expose un
    // resetZoom() sur le root pour que les autres handlers (thumbs, variant)
    // puissent réinitialiser la transform avant de swap l'image.
    wireZoom(root);

    wireThumbs(root);
    prefetchThumbs(root);

    // Init le bloc LLD (.product-louer-block) s'il y en a un — la fonction est
    // exposée par assets/product-louer-block.js.
    if (typeof window.initProductLouerBlocks === 'function') {
      window.initProductLouerBlocks(root);
    }

    // Mirror du prix LLD mensuel vers le sticky footer du drawer.
    wireStickyLld(root);

    // Masque le sticky footer tant que les CTAs principaux sont visibles
    // dans la zone scrollable, l'affiche une fois scrollés hors vue.
    wireStickyVisibility(root);
  }

  // IntersectionObserver sur .quick-preview__ctas pour piloter la visibilité
  // du sticky footer. Le scroll container est .quick-preview__scroll (sibling).
  function wireStickyVisibility(root) {
    if (typeof IntersectionObserver !== 'function') return;
    var ctas = root.querySelector('.quick-preview__ctas');
    var sticky = root.querySelector('.quick-preview__sticky');
    if (!ctas || !sticky) return;
    var scrollEl = root.closest('.quick-preview__scroll');

    // État initial : au chargement du drawer, les CTAs sont visibles → sticky caché.
    sticky.classList.add('quick-preview__sticky--hidden');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          sticky.classList.toggle('quick-preview__sticky--hidden', entry.isIntersecting);
        });
      },
      { root: scrollEl || null, threshold: 0 },
    );
    observer.observe(ctas);
  }

  // Observe [data-louer-monthly] — peuplé asynchronement par product-louer-block.js
  // après réponse de greenleaze — et mirror sa valeur vers [data-qp-sticky-lld]
  // du sticky footer. Masque la ligne sticky tant que le prix est "--".
  function wireStickyLld(root) {
    var src = root.querySelector('[data-louer-monthly]');
    var dest = root.querySelector('[data-qp-sticky-lld]');
    if (!src || !dest) return;
    var stickyLine = dest.closest('.quick-preview__sticky-lld') || dest.parentNode;

    function sync() {
      var value = (src.textContent || '').trim();
      dest.textContent = value;
      if (stickyLine) {
        if (value === '' || value === '--') {
          stickyLine.setAttribute('hidden', '');
        } else {
          stickyLine.removeAttribute('hidden');
        }
      }
    }
    sync();
    new MutationObserver(sync).observe(src, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  // Zoom plein écran inline — calqué sur assets/product-gallery.js (méthodes
  // setupZoomAndPan / handleZoom / setZoom / applyTransform / constrainPan /
  // resetZoom / openFullscreen / closeFullscreen). Adapté à un seul `<img>`
  // ([data-qp-main-image]) au lieu d'un carousel Flickity multi-slides.
  // Bascule la classe `quick-preview--fullscreen` sur le `.quick-preview`
  // racine + `gallery-fullscreen-active` sur <body> (réutilisé de la FP).
  function wireZoom(root) {
    var media = root.querySelector('.quick-preview__media');
    var container = root.querySelector('[data-qp-main]');
    var img = root.querySelector('[data-qp-main-image]');
    var openBtn = root.querySelector('[data-qp-fullscreen]');
    var closeBtn = root.querySelector('[data-qp-fullscreen-close]');
    if (!media || !container || !img) return;

    var state = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
    };
    var initialDistance = 0;
    var initialScale = 1;
    var originalSizes = null;
    // Stockage du point d'ancrage DOM d'origine du `.quick-preview__media` :
    // en plein écran on le déplace dans <body> pour échapper au containing
    // block créé par le `transform: translateX(...)` de `.qp-panel`. Sans ça
    // `position: fixed` est relatif au panel et ne couvre pas le viewport.
    var mediaParent = null;
    var mediaNextSibling = null;

    function isFullscreen() {
      return media.classList.contains('quick-preview__media--fullscreen');
    }

    function applyTransform() {
      img.style.transformOrigin = 'center center';
      img.style.transition = state.isDragging ? 'none' : 'transform 0.2s ease-out';
      img.style.transform =
        'scale(' +
        state.scale +
        ') translate(' +
        state.translateX / state.scale +
        'px, ' +
        state.translateY / state.scale +
        'px)';
    }

    function constrainPan() {
      if (state.scale <= 1) {
        state.translateX = 0;
        state.translateY = 0;
        applyTransform();
        return;
      }
      var rect = container.getBoundingClientRect();
      var maxX = (rect.width * state.scale - rect.width) / 2;
      var maxY = (rect.height * state.scale - rect.height) / 2;
      state.translateX = Math.max(-maxX, Math.min(maxX, state.translateX));
      state.translateY = Math.max(-maxY, Math.min(maxY, state.translateY));
      applyTransform();
    }

    function setZoom(scale) {
      state.scale = scale;
      applyTransform();
      constrainPan();
      container.classList.toggle('quick-preview__main--zoomed', state.scale > 1);
    }

    function handleZoom(delta, centerX, centerY) {
      var newScale = Math.max(1, Math.min(4, state.scale + delta));
      if (newScale === state.scale) return;
      var rect = container.getBoundingClientRect();
      var offsetX = centerX - rect.left - rect.width / 2;
      var offsetY = centerY - rect.top - rect.height / 2;
      var ratio = newScale / state.scale;
      state.translateX = state.translateX * ratio - offsetX * (ratio - 1);
      state.translateY = state.translateY * ratio - offsetY * (ratio - 1);
      setZoom(newScale);
    }

    function resetZoom() {
      state.scale = 1;
      state.translateX = 0;
      state.translateY = 0;
      state.isDragging = false;
      img.style.transform = 'none';
      img.style.transformOrigin = 'center center';
      img.style.transition = 'transform 0.2s ease-out';
      container.classList.remove('quick-preview__main--zoomed');
    }

    function openFullscreen() {
      if (isFullscreen()) return;
      // Sauvegarde puis force sizes en 100vw pour que le navigateur charge la
      // version HD (le srcset inclut 2048w).
      originalSizes = img.getAttribute('sizes');
      img.setAttribute('sizes', '100vw');
      // Téléporte la zone media dans <body> pour échapper au containing
      // block du `.qp-panel` (transform). On retient parent + sibling pour
      // pouvoir replacer au close.
      mediaParent = media.parentNode;
      mediaNextSibling = media.nextSibling;
      document.body.appendChild(media);
      media.classList.add('quick-preview__media--fullscreen');
      document.body.classList.add('gallery-fullscreen-active');
      resetZoom();
      if (closeBtn) closeBtn.focus();
    }

    function closeFullscreen() {
      if (!isFullscreen()) return;
      resetZoom();
      media.classList.remove('quick-preview__media--fullscreen');
      document.body.classList.remove('gallery-fullscreen-active');
      // Replace la zone media à son emplacement d'origine dans le panel.
      if (mediaParent) {
        mediaParent.insertBefore(media, mediaNextSibling);
        mediaParent = null;
        mediaNextSibling = null;
      }
      if (originalSizes != null) {
        img.setAttribute('sizes', originalSizes);
        originalSizes = null;
      }
      if (openBtn) openBtn.focus();
    }

    if (openBtn) openBtn.addEventListener('click', openFullscreen);
    if (closeBtn) closeBtn.addEventListener('click', closeFullscreen);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isFullscreen()) {
        closeFullscreen();
      }
    });

    // Wheel zoom (fullscreen uniquement, sinon laisse le scroll natif).
    container.addEventListener(
      'wheel',
      function (e) {
        if (!isFullscreen()) return;
        e.preventDefault();
        var delta = e.deltaY > 0 ? -0.1 : 0.1;
        handleZoom(delta, e.clientX, e.clientY);
      },
      { passive: false },
    );

    // Touch — pinch (2 doigts) + pan (1 doigt si zoomé).
    container.addEventListener(
      'touchstart',
      function (e) {
        if (!isFullscreen()) return;
        if (e.touches.length === 2) {
          var t1 = e.touches[0];
          var t2 = e.touches[1];
          initialDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          initialScale = state.scale;
          e.preventDefault();
        } else if (e.touches.length === 1 && state.scale > 1) {
          state.isDragging = true;
          state.startX = e.touches[0].clientX - state.translateX;
          state.startY = e.touches[0].clientY - state.translateY;
          e.preventDefault();
        }
      },
      { passive: false },
    );

    container.addEventListener(
      'touchmove',
      function (e) {
        if (!isFullscreen()) return;
        if (e.touches.length === 2) {
          var t1 = e.touches[0];
          var t2 = e.touches[1];
          var dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          var scale = (dist / initialDistance) * initialScale;
          setZoom(Math.max(1, Math.min(4, scale)));
          e.preventDefault();
        } else if (e.touches.length === 1 && state.isDragging) {
          state.translateX = e.touches[0].clientX - state.startX;
          state.translateY = e.touches[0].clientY - state.startY;
          applyTransform();
          e.preventDefault();
        }
      },
      { passive: false },
    );

    container.addEventListener('touchend', function (e) {
      if (!isFullscreen()) return;
      state.isDragging = false;
      if (e.touches.length === 0) constrainPan();
    });

    // Mouse pan — listeners mousemove/mouseup sur document (souris peut sortir
    // du conteneur pendant le drag).
    container.addEventListener('mousedown', function (e) {
      if (!isFullscreen() || state.scale <= 1) return;
      state.isDragging = true;
      state.startX = e.clientX - state.translateX;
      state.startY = e.clientY - state.translateY;
      e.preventDefault();
    });

    document.addEventListener('mousemove', function (e) {
      if (!state.isDragging) return;
      state.translateX = e.clientX - state.startX;
      state.translateY = e.clientY - state.startY;
      applyTransform();
    });

    document.addEventListener('mouseup', function () {
      if (state.isDragging) {
        state.isDragging = false;
        constrainPan();
      }
    });

    // Expose un resetZoom() pour les handlers thumbs/variant qui swap l'image.
    root.qpResetZoom = resetZoom;
  }

  // Replace <script> nodes with fresh clones to force execution. Browsers ignore
  // script tags inserted via innerHTML / DOMParser, so we need this for any
  // inline JS that ships inside the injected product section (notably
  // greenleaze-abonnement-product.liquid which sets up the iframe modal).
  function executeInlineScripts(root) {
    var scripts = root.querySelectorAll('script');
    scripts.forEach(function (old) {
      var fresh = document.createElement('script');
      // Copy attributes
      for (var i = 0; i < old.attributes.length; i++) {
        var a = old.attributes[i];
        fresh.setAttribute(a.name, a.value);
      }
      // Copy inline content
      if (old.textContent) fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    });
  }

  function parseVariants(root) {
    var el = root.querySelector('[data-qp-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  function parseJsonScript(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function parseTaillesByVariant(root) {
    return parseJsonScript(root, '[data-qp-taille-by-variant]');
  }

  function parseReservedByVariant(root) {
    return parseJsonScript(root, '[data-qp-reserved-by-variant]');
  }

  function wireVariants(root, variants, onChange) {
    if (typeof theme === 'undefined' || !theme.Variants) return;
    if (!root.querySelector('[data-variant-input]')) return;

    new theme.Variants({
      container: root,
      variants: variants,
      singleOptionSelector: '[data-variant-input]',
      originalSelectorId: '[data-product-select]',
      enableHistoryState: false,
    });

    root.addEventListener('variantChange', function (evt) {
      var next = evt.detail && evt.detail.variant;
      if (next) onChange(next);
    });
  }

  function wireThumbs(root) {
    var main = root.querySelector('[data-qp-main-image]');
    if (!main) return;
    var thumbs = root.querySelectorAll('[data-qp-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-qp-image');
        if (!src) return;
        if (typeof root.qpResetZoom === 'function') root.qpResetZoom();
        var hd = thumb.getAttribute('data-qp-image-hd');
        // Conserve un srcset (low + hd) pour que sizes=100vw en plein écran
        // demande automatiquement la HD au navigateur.
        if (hd) {
          main.setAttribute('srcset', src + ' 900w, ' + hd + ' 2048w');
          main.setAttribute('data-qp-image-hd', hd);
        } else {
          main.removeAttribute('srcset');
        }
        main.src = src;
        thumbs.forEach(function (t) {
          t.removeAttribute('aria-current');
        });
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  // Préfetche les versions 900px (data-qp-image) de chaque thumb pour que
  // le swap de l'image principale au clic soit instantané (cache HTTP hit).
  // `<link rel="prefetch" as="image">` est exécuté en idle-priority par le
  // navigateur, ne bloque pas le chargement initial du drawer. Idempotent
  // grâce au Set global qui dédoublonne les URLs déjà prefetchées.
  var prefetched = new Set();
  function prefetchThumbs(root) {
    var thumbs = root.querySelectorAll('[data-qp-thumb]');
    thumbs.forEach(function (thumb) {
      var url = thumb.getAttribute('data-qp-image');
      if (!url || prefetched.has(url)) return;
      prefetched.add(url);
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  }

  function updateForVariant(root, variant) {
    if (!variant) return;

    var price = root.querySelector('[data-qp-price]');
    var compare = root.querySelector('[data-qp-compare]');
    var discount = root.querySelector('[data-qp-discount]');
    var savingsLine = root.querySelector('[data-qp-savings-line]');
    var savings = root.querySelector('[data-qp-savings]');
    var stickyPrice = root.querySelector('[data-qp-sticky-price]');
    var atcAll = root.querySelectorAll('[data-qp-atc], [data-qp-atc-sticky]');
    var idInput = root.querySelector('[data-qp-id-input]');
    var tailleEl = root.querySelector('[data-qp-taille-cycliste]');
    var main = root.querySelector('[data-qp-main-image]');
    var lldEl = root.querySelector('[data-qp-greenleaze]');

    // Source of truth pour la soumission du form : le hidden input doit suivre
    // la chip de taille active, sinon AjaxProduct serialize l'ancien id.
    if (idInput) idInput.value = variant.id;

    // Taille recommandée par variant — lookup table sérialisée côté Liquid
    // dans [data-qp-taille-by-variant].
    if (tailleEl) {
      var taillesByVariant = parseTaillesByVariant(root);
      var taille = taillesByVariant[variant.id];
      if (taille != null) tailleEl.textContent = taille;
    }

    if (price) price.textContent = formatMoney(variant.price);
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (compare) {
      if (onSale) {
        compare.textContent = formatMoney(variant.compare_at_price) + ' neuf';
        compare.removeAttribute('hidden');
      } else {
        compare.setAttribute('hidden', '');
      }
    }
    if (discount) {
      if (onSale) {
        var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        discount.textContent = '−' + pct + '%';
        discount.removeAttribute('hidden');
      } else {
        discount.setAttribute('hidden', '');
      }
    }
    if (savingsLine && savings) {
      if (onSale) {
        savings.textContent = formatMoney(variant.compare_at_price - variant.price);
        savingsLine.removeAttribute('hidden');
      } else {
        savingsLine.setAttribute('hidden', '');
      }
    }

    var reserved = parseReservedByVariant(root)[variant.id] === true;
    atcAll.forEach(function (btn) {
      btn.setAttribute('data-qp-variant-id', variant.id);
      btn.disabled = !variant.available;
      if (variant.available) btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', 'true');
      btn.textContent = variant.available
        ? btn.dataset.atcText
        : reserved
          ? btn.dataset.reservedText
          : btn.dataset.soldText;
    });

    if (main && variant.featured_image && variant.featured_image.src) {
      if (typeof root.qpResetZoom === 'function') root.qpResetZoom();
      main.src = variant.featured_image.src;
      main.removeAttribute('srcset');
    }

    if (lldEl) {
      lldEl.setAttribute('data-variant-id', String(variant.id));
      var taxable = variant.taxable !== false;
      var lldPrice = taxable ? Math.round(variant.price / 1.2) : variant.price;
      lldEl.setAttribute('data-price', String(lldPrice));
      try {
        if (typeof lldEl.render === 'function') lldEl.render();
        else lldEl.dispatchEvent(new CustomEvent('greenleaze:refresh'));
      } catch (e) {
        /* noop */
      }
    }
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    var value = cents / 100;
    var fmt = moneyFormat || '{{amount}} €';
    return fmt
      .replace('{{amount_no_decimals}}', Math.round(value).toLocaleString('fr-FR'))
      .replace('{{amount_with_comma_separator}}', value.toFixed(2).replace('.', ','))
      .replace(
        '{{amount_no_decimals_with_space_separator}}',
        Math.round(value).toLocaleString('fr-FR').replace(/,/g, ' '),
      )
      .replace('{{amount}}', value.toFixed(2).replace('.', ','));
  }
})();
